import React, { useMemo, useState } from 'react';
import { FaEnvelope } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import ContactForm from '../components/blocks/ContactForm';
import TutorialHub from '../components/tutorial/TutorialHub';
import { getPublicHelpFaqIds } from '../config/helpFaqs';

const Help = () => {
  const [openAccordion, setOpenAccordion] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const { t } = useTranslation();

  const faqs = useMemo(
    () =>
      getPublicHelpFaqIds().map((id) => ({
        id,
        question: t(`help.faqs.${id}.question`),
        answer: t(`help.faqs.${id}.answer`)
      })),
    [t]
  );

  const toggleAccordion = (index) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  return (
    <div className="bg-canvas min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-10">
          <TutorialHub t={t} />

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 items-start">
            <section>
              <div className="mb-4">
                <p className="text-sm font-semibold text-link uppercase tracking-wide">
                  {t('help.tutorialHub.supportEyebrow')}
                </p>
                <h2 className="text-xl font-semibold text-ink mt-1">{t('help.faq')}</h2>
                <p className="text-ink-muted mt-2 text-sm text-justify max-w-2xl">{t('help.faqIntro')}</p>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={faq.id} className="bg-surface rounded-lg shadow-sm">
                    <button
                      type="button"
                      aria-expanded={openAccordion === index}
                      className="w-full px-6 py-4 text-left focus:outline-none"
                      onClick={() => toggleAccordion(index)}
                    >
                      <div className="flex justify-between items-center gap-3">
                        <h3 className="font-roboto text-lg font-medium text-ink">{faq.question}</h3>
                        <svg
                          className={`w-5 h-5 shrink-0 text-ink-subtle transform transition-transform duration-200 ease-out motion-reduce:transition-none ${
                            openAccordion === index ? 'rotate-180' : ''
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>
                    <div
                      className={`grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none ${
                        openAccordion === index ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                      }`}
                    >
                      <div className="min-h-0 overflow-hidden">
                        <div className="px-6 pb-4">
                          <p className="font-roboto text-ink-muted text-justify">{faq.answer}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <aside className="bg-surface rounded-lg shadow-sm border border-edge p-5 sticky top-4">
              <h2 className="text-lg font-semibold text-ink">{t('help.contact')}</h2>
              <p className="text-sm text-ink-muted mt-2 mb-4 text-justify">
                {t('help.tutorialHub.contactNote')}
              </p>
              <button
                onClick={() => setShowContactForm(true)}
                className="flex items-center justify-center w-full bg-primary text-primary-foreground py-2.5 px-4 rounded-full hover:bg-primary-hover transition-colors font-medium"
              >
                <FaEnvelope className="mr-2" />
                {t('help.contactButton')}
              </button>
            </aside>
          </div>
        </div>
      </div>

      <ContactForm isOpen={showContactForm} onClose={() => setShowContactForm(false)} />
    </div>
  );
};

export default Help;
